/**
 * The prompt patterns a user may select, and the write that stores a selection (TASK-0039).
 *
 * The list is level-scoped and carries the server's own suggestion flag, because the policy is the server's
 * (`PromptPatternSuggestionPolicy`) and a client that recomputed it would be a second implementation of one rule. The
 * hook does not decide what is selected — the wizard's own state does — so a restored or deliberately modified selection
 * is never overwritten by a refetch.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { adminPromptPatternApi, promptPatternApi } from '@/features/wizard/api/promptPatternApi';
import { queryKeys } from '@/lib/queryKeys';
import type {
  CreatePromptPatternPayload,
  PromptLevel,
  SelectablePromptPattern,
  UpdatePromptPatternPayload,
} from '@/types/api';

/**
 * Lifetime of a level's pattern list in the cache.
 *
 * The catalog is administrator-edited content rather than account data, so it is cached like the wizard's step catalogue;
 * a change an administrator makes is picked up on the next mount rather than mid-typing, which is the same contract the
 * framework step list has.
 */
const PATTERN_STALE_TIME_MS = 60 * 1000;

/**
 * Reads the patterns offered for one prompt level.
 *
 * @param level - Prompt level the wizard is being written at.
 * @param enabled - Whether to run the query at all; a screen with no draft yet passes `false`.
 * @returns Query result carrying the offered patterns, in suggestion order, with a suggestion flag on each.
 */
export function useSelectablePromptPatterns(level: PromptLevel, enabled = true) {
  return useQuery<SelectablePromptPattern[]>({
    queryKey: queryKeys.selectablePromptPatterns(level),
    enabled,
    staleTime: PATTERN_STALE_TIME_MS,
    queryFn: () => promptPatternApi.list(level),
  });
}

/**
 * Reads the administrator's prompt-pattern catalog.
 *
 * @param includeDeleted - Whether soft-deleted patterns are included; the panel passes `true` so it can show what was
 *   removed and still has references.
 * @param enabled - Whether to run the query at all; only an administrator may read it.
 */
export function usePromptPatternCatalog(includeDeleted = false, enabled = true) {
  return useQuery({
    queryKey: queryKeys.promptPatterns(includeDeleted),
    enabled,
    queryFn: () => adminPromptPatternApi.list(includeDeleted),
  });
}

/** Creates a pattern and refreshes the catalog, so the panel shows what the server stored. */
export function useCreatePromptPattern() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreatePromptPatternPayload) => adminPromptPatternApi.create(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'prompt-patterns'] });
    },
  });
}

/** Edits a pattern and refreshes the catalog. */
export function useUpdatePromptPattern() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      patternId,
      payload,
    }: {
      patternId: string;
      payload: UpdatePromptPatternPayload;
    }) => adminPromptPatternApi.update(patternId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'prompt-patterns'] });
    },
  });
}

/** Enables or disables a pattern and refreshes the catalog. */
export function useSetPromptPatternState() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ patternId, isActive }: { patternId: string; isActive: boolean }) =>
      adminPromptPatternApi.setState(patternId, isActive),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'prompt-patterns'] });
    },
  });
}

/** Soft-deletes a pattern and refreshes the catalog. */
export function useDeletePromptPattern() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (patternId: string) => adminPromptPatternApi.remove(patternId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'prompt-patterns'] });
    },
  });
}

/** Brings a soft-deleted pattern back and refreshes the catalog. */
export function useRestorePromptPattern() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (patternId: string) => adminPromptPatternApi.restore(patternId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'prompt-patterns'] });
    },
  });
}
